import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  useGetAllUsersQuery,
  useDeactivateUserMutation,
  useReactivateUserMutation,
  useAdminResetPasswordMutation,
  useStaffForgotPasswordMutation,
} from '../../apis/konektApi';
import { useTranslation } from 'react-i18next';
import TableSkeleton from '../../components/skeletons/TableSkeleton';
import { AntDesignStyledDataGridPro } from '../../components/AntDesignStyle';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Switch,
} from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import MainCard from '../../components/MainCard';
import { dispatch } from '../../store';
import { openSnackbar } from '../../store/reducers/snackbar';
import StaffContextMenu from './StaffContextMenu';
import StaffBulkActionToolbar from './StaffBulkActionToolbar';

const STAFF_EDIT_ENABLED = process.env.REACT_APP_STAFF_EDIT_ENABLED === 'true';

const UserListPage = () => {
  const { data, error, isLoading, refetch } = useGetAllUsersQuery();
  const tenantId = useSelector((state: RootState) => state.login.tenantId ?? '');
  const { t } = useTranslation();
  const [deactivateUser] = useDeactivateUserMutation();
  const [reactivateUser] = useReactivateUserMutation();
  const [adminResetPassword] = useAdminResetPasswordMutation();
  const [staffForgotPassword] = useStaffForgotPasswordMutation();

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [toggleTarget, setToggleTarget] = useState<{ userId: string; isActive: boolean } | null>(
    null
  );

  if (isLoading) return <TableSkeleton rows={8} columns={5} />;

  const handleResetPassword = async (userId: string) => {
    const user = data?.find((u) => u.id === userId);
    if (!user) return;
    try {
      if (STAFF_EDIT_ENABLED) {
        await adminResetPassword({ userId }).unwrap();
      } else {
        await staffForgotPassword({ username: user.username, companyName: tenantId }).unwrap();
      }
      dispatch(
        openSnackbar({
          open: true,
          message: t('staff.reset_sent_success', { email: user.email }),
          variant: 'alert',
          alert: { color: 'success' },
          close: false,
        })
      );
    } catch {
      dispatch(
        openSnackbar({
          open: true,
          message: t('errors.generic_error'),
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        })
      );
    }
  };

  const handleDeactivate = async (userId: string) => {
    const user = data?.find((u) => u.id === userId);
    if (!user) return;
    try {
      await deactivateUser({ id: userId }).unwrap();
      dispatch(
        openSnackbar({
          open: true,
          message: t('staff.deactivated_success', { name: user.names }),
          variant: 'alert',
          alert: { color: 'success' },
          close: false,
        })
      );
      refetch();
    } catch {
      dispatch(
        openSnackbar({
          open: true,
          message: t('errors.generic_error'),
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        })
      );
    }
  };

  const handleReactivate = async (userId: string) => {
    const user = data?.find((u) => u.id === userId);
    if (!user) return;
    try {
      await reactivateUser({ id: userId }).unwrap();
      dispatch(
        openSnackbar({
          open: true,
          message: t('staff.reactivated_success', { name: user.names }),
          variant: 'alert',
          alert: { color: 'success' },
          close: false,
        })
      );
      refetch();
    } catch {
      dispatch(
        openSnackbar({
          open: true,
          message: t('errors.generic_error'),
          variant: 'alert',
          alert: { color: 'error' },
          close: false,
        })
      );
    }
  };

  const handleConfirmToggle = async () => {
    if (!toggleTarget) return;
    if (toggleTarget.isActive) {
      await handleDeactivate(toggleTarget.userId);
    } else {
      await handleReactivate(toggleTarget.userId);
    }
    setToggleTarget(null);
  };

  const handleDeactivateAll = async (ids: string[]) => {
    for (const id of ids) {
      await handleDeactivate(id);
    }
    setSelectedIds([]);
  };

  const handleResendInviteAll = async (ids: string[]) => {
    for (const id of ids) {
      await handleResetPassword(id);
    }
  };

  const handleExportCSV = () => {
    if (!data) return;
    const selected = data.filter((u) => selectedIds.includes(u.id));
    const rows = selected.map((u) => `${u.names},${u.email},${u.role},${u.cellNumber}`);
    const csv = [`Names,Email,Role,Cell Number`, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'staff-export.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const renderActionsCell = (params: any) => (
    <StaffContextMenu userId={params.row.id} onResetPassword={handleResetPassword} />
  );

  const CustomToolbar = () => (
    <GridToolbarContainer>
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Box sx={{ flex: 1 }} />
      <GridToolbarQuickFilter />
    </GridToolbarContainer>
  );

  const toggleConfirmText = () => {
    if (!toggleTarget) return '';
    const user = data?.find((u) => u.id === toggleTarget.userId);
    const name = user?.names || '';
    return toggleTarget.isActive
      ? t('staff.deactivate_confirm', { name })
      : t('staff.reactivate_confirm', { name });
  };

  const userListColumn = [
    { field: 'names', headerName: t('tables.names'), width: 210, sortable: false },
    { field: 'cellNumber', headerName: t('tables.cell_number'), width: 150, sortable: false },
    { field: 'email', headerName: t('tables.email'), width: 250, sortable: false },
    { field: 'role', headerName: t('tables.role'), width: 100, sortable: false },
    {
      field: 'isActive',
      headerName: t('tables.status') || 'Status',
      width: 90,
      sortable: false,
      renderCell: (params: any) => {
        const active = params.row.isActive !== false;
        return (
          <Switch
            checked={active}
            onChange={() => setToggleTarget({ userId: params.row.id, isActive: active })}
            color="success"
            size="small"
          />
        );
      },
    },
    {
      field: 'details',
      headerName: t('tables.details'),
      width: 80,
      sortable: false,
      renderCell: renderActionsCell,
    },
  ];

  if (!data || !data.length || error) {
    return <Box>{t('errors.users_not_found')}</Box>;
  }

  return (
    <MainCard title={t('titles.user_management')}>
      <StaffBulkActionToolbar
        selectedIds={selectedIds}
        onDeactivateAll={handleDeactivateAll}
        onResendInviteAll={handleResendInviteAll}
        onExportCSV={handleExportCSV}
      />
      <Box sx={{ height: '600px' }}>
        <AntDesignStyledDataGridPro
          pagination
          pageSize={20}
          rowsPerPageOptions={[20]}
          columns={userListColumn}
          rows={data}
          components={{ Toolbar: CustomToolbar }}
          componentsProps={{ toolbar: { showQuickFilter: true } }}
          loading={isLoading}
          checkboxSelection
          disableSelectionOnClick
          rowThreshold={0}
          initialState={{}}
          onSelectionModelChange={(model) => setSelectedIds(model as string[])}
        />
      </Box>

      <Dialog open={Boolean(toggleTarget)} onClose={() => setToggleTarget(null)}>
        <DialogTitle>{t('actions.confirm')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{toggleConfirmText()}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleTarget(null)}>{t('cancel_btn')}</Button>
          <Button
            color={toggleTarget?.isActive ? 'error' : 'success'}
            variant="contained"
            onClick={handleConfirmToggle}
          >
            {t('actions.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </MainCard>
  );
};

export default UserListPage;
